import { useState, useRef, useCallback, useEffect } from 'react';
import { X, Loader2, Search } from 'lucide-react';
import { api } from '@/lib/api';

export interface CatalogItem {
  id: string;
  make: string;
  model: string;
  version: string | null;
  yearFrom: number;
  yearTo: number | null;
  engineSize: string | null;
  fuelType: string | null;
}

interface Props {
  selected: CatalogItem[];
  onChange: (items: CatalogItem[]) => void;
}

function formatYearRange(from: number, to: number | null) {
  if (!to || to === from) return String(from);
  return `${from}-${to}`;
}

function formatLabel(item: CatalogItem) {
  let label = `${item.make} ${item.model}`;
  if (item.version) label += ` ${item.version}`;
  label += ` (${formatYearRange(item.yearFrom, item.yearTo)})`;
  return label;
}

export function CatalogMultiSelect({ selected, onChange }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get('/vehicle-catalog/search', { params: { q } });
      const items = (data.results ?? []) as CatalogItem[];
      // Filter out already-selected items
      const selectedIds = new Set(selected.map((s) => s.id));
      setResults(items.filter((item) => !selectedIds.has(item.id)));
      setShowDropdown(true);
    } catch {
      setResults([]);
      setShowDropdown(true);
    } finally {
      setLoading(false);
    }
  }, [selected]);

  function handleChange(text: string) {
    setQuery(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (text.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    timerRef.current = setTimeout(() => { search(text); }, 300);
  }

  function handleSelect(item: CatalogItem) {
    onChange([...selected, item]);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  }

  function handleRemove(id: string) {
    onChange(selected.filter((s) => s.id !== id));
  }

  return (
    <div ref={wrapperRef} className="relative">
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selected.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
            >
              {item.make} {item.model} ({formatYearRange(item.yearFrom, item.yearTo)})
              <button
                type="button"
                onClick={() => handleRemove(item.id)}
                className="rounded-full p-0.5 hover:bg-primary/20"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-3 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
          placeholder="Buscar modelo (ex: Gol, Civic...)"
          className="w-full rounded-lg border border-border bg-muted py-2.5 pl-8 pr-4 text-sm outline-none focus:border-primary"
        />
        {loading && (
          <Loader2 size={14} className="absolute right-3 top-3 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-md">
          {results.length > 0 ? (
            results.map((item) => (
              <button
                key={item.id}
                type="button"
                className="flex w-full flex-col px-3 py-2 text-left hover:bg-muted/60"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(item)}
              >
                <span className="text-sm font-semibold">
                  {item.make} {item.model}
                </span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  {item.version && (
                    <span className="max-w-[180px] truncate">{item.version}</span>
                  )}
                  <span className="font-medium text-foreground/70">
                    {formatYearRange(item.yearFrom, item.yearTo)}
                  </span>
                  {item.fuelType && <span className="capitalize">{item.fuelType}</span>}
                </span>
              </button>
            ))
          ) : !loading ? (
            /* Fica a mao: resultado de autocomplete, nao estado vazio de tela.
               O bloco do kit empurraria o campo de busca para fora da vista
               enquanto a pessoa ainda esta digitando. */
            <div className="px-3 py-3 text-center text-sm text-muted-foreground">
              Nenhum modelo encontrado.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
